// mid-wi8v (JP 2026-11-03): conversion funnel names for Fathom, so the PostHog funnel can be checked.
// Fathom is cookieless and gets names only, so it needs no analytics consent. Fathom records events
// only from the browser: backend conversions wait server-side until this browser collects them
// (POST /api/analytics/fathom-pending, each name handed out once). Fathom names can never be renamed.
// Visitors and buyers only; agents, staff, intermediaries and logged-out non-visitors send nothing.
import axios from 'axios';
import { getAnon } from './anonSession';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SENT_KEY = 'mid_fathom_sent';
const DEBOUNCE_MS = 3000; // background milestones land a moment after the response
const MIN_GAP_MS = 15000; // at most one collect call per 15 s; later bursts wait, never drop

export const FATHOM_FRONTEND_EVENTS = new Set([
  'dna_started',
  'dna_step_conversa', 'dna_step_zona', 'dna_step_dilemas', 'dna_step_estilo_vida', 'dna_step_estilo_visual', 'dna_step_resultado',
  'dna_account_shown', 'dna_account_done',
]);

let actor = null;
let timer = null;
let lastCollect = -Infinity;

const fathom = () => (typeof window !== 'undefined' && typeof window.fathom?.trackEvent === 'function' ? window.fathom : null);

export function setFathomActor(value) {
  actor = ['visitor', 'buyer'].includes(value) ? value : null;
  if (!actor) clearTimeout(timer);
}

/** A frontend funnel step, once per browser; no properties ever reach Fathom. */
export function sendFathomEvent(name) {
  const sdk = fathom();
  if (!actor || !sdk || !FATHOM_FRONTEND_EVENTS.has(name)) return false;
  let sent = [];
  try { sent = JSON.parse(localStorage.getItem(SENT_KEY)) || []; } catch { /* blocked or invalid storage */ }
  if (!Array.isArray(sent)) sent = [];
  if (sent.includes(name)) return false;
  sdk.trackEvent(name);
  try { localStorage.setItem(SENT_KEY, JSON.stringify([...sent, name])); } catch { /* private mode */ }
  return true;
}

/** Collects pending backend conversions. Without the Fathom script (ad blocker) they stay pending. */
export async function flushFathomConversions() {
  const sdk = fathom();
  if (!actor || !sdk) return [];
  lastCollect = Date.now();
  try {
    const { anonId } = getAnon();
    const { data } = await axios.post(`${API_URL}/api/analytics/fathom-pending`, anonId ? { anon_id: anonId } : {},
      { withCredentials: true, timeout: 4000 });
    const names = Array.isArray(data?.events) ? data.events.filter((n) => typeof n === 'string') : [];
    names.forEach((n) => sdk.trackEvent(n));
    return names;
  } catch {
    return [];
  }
}

export function scheduleFathomFlush() {
  if (!actor || !fathom()) return;
  clearTimeout(timer);
  timer = setTimeout(flushFathomConversions, Math.max(DEBOUNCE_MS, lastCollect + MIN_GAP_MS - Date.now()));
}

/** Response interceptor for axios and the api instance: any successful API call may have produced a conversion. */
export function fathomResponseHook(response) {
  const url = `${response?.config?.baseURL || ''}${response?.config?.url || ''}`;
  if (url.includes('/api/') && !/\/analytics\/|\/chat\/unread/.test(url)) scheduleFathomFlush();
  return response;
}
